<template>
  <div class="landing-container">
    <header class="hero-section">
      <div class="hero-content">
        <h1 class="app-title">
          <span>AIが紡ぎ出す、</span>
          <span>あなただけの物語との出会い。</span>
        </h1>
        <p class="app-subtitle">
          「小説家になろう」の膨大な作品の海から、<br>
          AIがあなたの好みを学習し、最高の物語を推薦します。
        </p>
        <div class="cta-buttons">
          <router-link to="/home" class="cta-button primary">
            まずはゲストで体験する
          </router-link>
          <button @click="handleAuthRedirect" class="cta-button secondary">
            {{ authButtonText }}
          </button>
        </div>
        <p class="login-prompt">ログインすると評価が保存され、推薦精度が向上します。</p>
      </div>
    </header>

    <main>
      <section class="how-to-use-section">
        <h2 class="section-title">カンタン3ステップで、未知の傑作と出会おう</h2>
        <div class="steps-grid">
          <div class="step-card fade-in">
            <div class="step-icon">📖</div>
            <h3 class="step-number">STEP 1</h3>
            <h4 class="step-title">小説をスワイプ評価</h4>
            <p class="step-description">
              表示される小説を直感的に左右へスワイプ。あなたの「好き」「苦手」をAIが学習します。
            </p>
          </div>
          <div class="step-card fade-in">
            <div class="step-icon">🤖</div>
            <h3 class="step-number">STEP 2</h3>
            <h4 class="step-title">AIが好みを分析</h4>
            <p class="step-description">
              あなたの評価をもとに、AIが好きなジャンルやキーワードを分析。推薦リストをパーソナライズします。
            </p>
          </div>
          <div class="step-card fade-in">
            <div class="step-icon">✨</div>
            <h3 class="step-number">STEP 3</h3>
            <h4 class="step-title">新たな物語を発見</h4>
            <p class="step-description">
              使えば使うほど賢くなるAIが、あなたのための特別な一作を提案。最高の読書体験が待っています。
            </p>
          </div>
        </div>
      </section>

      <section class="features-section">
        <h2 class="section-title">主な機能</h2>
        <div class="features-grid">
          <div class="feature-card fade-in">
            <div class="feature-icon">🔍</div>
            <h3 class="feature-title">直感的なスワイプ評価</h3>
            <p class="feature-description">
              表示される小説をTinderのようにスワイプするだけで、あなたの好みをAIが学習。使えば使うほど、推薦の精度が向上します。
            </p>
          </div>
          <div class="feature-card fade-in">
            <div class="feature-icon">📊</div>
            <h3 class="feature-title">多角的な評価とコメント</h3>
            <p class="feature-description">
              他のユーザーのスコア評価や感想を参考に、新しい視点で作品を発見。あなたも評価やコメントを投稿して、コミュニティに参加できます。
            </p>
          </div>
          <div class="feature-card fade-in">
            <div class="feature-icon">📚</div>
            <h3 class="feature-title">詳細な検索機能</h3>
            <p class="feature-description">
              キーワードやジャンルはもちろん、人気順や新着順での並び替えも可能。読みたい作品をピンポイントで見つけ出せます。
            </p>
          </div>
        </div>
      </section>
    </main>

    <footer class="landing-footer">
      <p>&copy; 2025 {{ yourAppName }} All rights reserved.</p>
    </footer>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();

// Vuexストアからログイン状態を取得
const isLoggedIn = computed(() => store.getters['auth/isAuthenticated']);

// ログイン状態に応じてボタンのテキストを動的に変更
const authButtonText = computed(() =>
  isLoggedIn.value ? 'アカウントページへ' : 'ログインして全機能を使う'
);

// ボタンクリック時のリダイレクト処理
const handleAuthRedirect = () => {
  if (isLoggedIn.value) {
    router.push('/account'); // ログイン済みならアカウントページへ
  } else {
    router.push('/login'); // 未ログインならログインページへ
  }
};

const yourAppName = 'Narou Novel Recommendation App';

// スクロールに応じたアニメーションのロジック (変更なし)
onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
      }
    });
  }, {
    threshold: 0.1
  });

  document.querySelectorAll('.fade-in').forEach(el => {
    observer.observe(el);
  });
});
</script>


<style scoped>
/* style部分は変更ありません */
.landing-container {
  font-family: 'Helvetica Neue', Arial, 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', Meiryo, sans-serif;
  color: #333;
  background-color: #fdfdfd;
}
.hero-section {
  background: linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url('https://source.unsplash.com/random/1600x900/?fantasy,anime') no-repeat center center;
  background-size: cover;
  color: white;
  text-align: center;
  padding: 120px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 90vh;
}
.hero-content {
  max-width: 800px;
}
.app-title {
  font-size: 3.2rem;
  font-weight: bold;
  margin-bottom: 25px;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.7);
  line-height: 1.3;
}
.app-title span {
  display: block;
}
.app-subtitle {
  font-size: 1.25rem;
  margin-bottom: 50px;
  line-height: 1.7;
}
.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}
/* ★★★ buttonタグにもスタイルが適用されるようにセレクタを調整 ★★★ */
.cta-button {
  display: inline-block;
  padding: 16px 35px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 2px solid transparent;
  cursor: pointer; /* buttonタグ用にカーソルスタイルを追加 */
}
.cta-button.primary {
  background-color: #4A90E2;
  color: white;
}
.cta-button.primary:hover {
  background-color: #357ABD;
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}
.cta-button.secondary {
  background-color: transparent;
  color: white;
  border-color: white;
}
.cta-button.secondary:hover {
  background-color: white;
  color: #4A90E2;
  transform: translateY(-3px);
}
.login-prompt {
  margin-top: 30px;
  font-size: 0.9rem;
  opacity: 0.9;
}
.section-title {
  font-size: 2.5rem;
  margin-bottom: 60px;
  position: relative;
  color: #333;
  text-align: center;
}
.section-title::after {
  content: '';
  display: block;
  width: 60px;
  height: 4px;
  background-color: #4A90E2;
  margin: 20px auto 0;
}
.how-to-use-section {
  padding: 80px 20px;
  background-color: #fff;
}
.steps-grid {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
}
.step-card {
  background-color: #f9f9f9;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  width: 300px;
  text-align: center;
}
.step-icon {
  font-size: 3.5rem;
  line-height: 1;
  margin-bottom: 15px;
}
.step-number {
  color: #4A90E2;
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 5px;
}
.step-title {
  font-size: 1.6rem;
  margin-bottom: 15px;
}
.step-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
}
.features-section {
  padding: 80px 20px;
  background-color: #f0f4f8;
}
.features-grid {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
}
.feature-card {
  background-color: white;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  width: 300px;
  text-align: center;
}
.feature-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}
.feature-title {
  font-size: 1.5rem;
  margin-bottom: 15px;
}
.feature-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
}
.landing-footer {
  text-align: center;
  padding: 20px;
  background-color: #333;
  color: #aaa;
  font-size: 0.9rem;
}
.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}
.fade-in.visible {
  opacity: 1;
  transform: translateY(0);
}
@media (max-width: 768px) {
  .app-title {
    font-size: 2.5rem;
  }
  .app-subtitle {
    font-size: 1.1rem;
  }
  .cta-buttons {
    flex-direction: column;
    gap: 15px;
  }
  .section-title {
    font-size: 2rem;
  }
}
</style>